import axios from 'axios';
import moment from 'moment';
import Notifications from 'react-notification-system-redux';
import { shapeData, CLEARED_CODING } from './common';
import * as constants from './types';
import { fetchTransactionsWithRules } from './transactionHelpers';

  const GENERIC_MATCH_ERROR = 'Could not match transaction';

  // POST /R1/:bookId/match/:guid answers 200 whether or not the match was accepted
  // (res.json(transfer) in BankDataAPI), so the outcome has to be read from the body
  // rather than the status code. Two failure shapes come back and they do not mean the
  // same thing (RWR-20040):
  //
  //   refused -- the reason is a string in `message`, written for the user. It is
  //   raised before codeTransactionByMatch runs (MATCH_NOT_OUTSTANDING), so nothing was
  //   applied in R1 and the reason is shown as-is:
  //     { statusCode: 400, errorType: 'Bad Request', message: 'This invoice has already...' }
  //
  //   failed -- something threw and `message` is the object it threw, which can carry
  //   internal detail, so those keep the generic message:
  //     { statusCode: 400, message: { statusCode: 500, message: '...', error: '...' } }
  //   The throw may have come from after codeTransactionByMatch created the receipt or
  //   payment -- the post-coding getInvoice/getBill, the transactionLinks check, the
  //   reconciliationStatus PATCH -- so this shape says the outcome is unknown, not that
  //   nothing happened.
  //
  // A success body carries no error statusCode.
  const matchFailure = (data) => {
    if (!data || typeof data !== 'object') return undefined;
    const { statusCode, message } = data;
    if (!statusCode || statusCode < 400) return undefined;
    return (statusCode === 400 && typeof message === 'string' && message.trim())
      ? { refused: true, message }
      : { refused: false, message: GENERIC_MATCH_ERROR };
  }

  // The row is matched optimistically before the request is sent, so a refusal has to
  // put it back: the transaction goes back to uncoded, the match is released for
  // another transaction to claim, and the Match button returns so the user can pick a
  // different one or dismiss it.
  const unwindMatch = (dispatch, getState, transaction) => {
    const { MatchId, BankDataGUID } = transaction;
    dispatch({
      type: constants.UPDATE_TRANSACTIONS,
      payload: { ...transaction, ...CLEARED_CODING, accountCodeId: undefined, accountType: undefined },
      // Selecting the match staged a coding; that coding is being discarded here, so
      // the row must not stay held back from the background refreshes.
      staged: false,
    });
    const { matches } = getState().transactions;
    dispatch({
      type: constants.UPDATE_MATCHES,
      payload: matches.map(item => (item.MatchId === MatchId || item.TransactionId === BankDataGUID)
        ? { ...item, TransactionId: null }
        : item),
    });
    dispatch(restoreMatchButton(BankDataGUID));
  }

  export function showError(message) {
    return Notifications.warning({ message });
  }

  // Every caller of this is staging a coding edit that has not been sent yet, so the
  // row is flagged and the background refreshes leave it alone until it is posted
  // (RWR-19694). The other UPDATE_TRANSACTIONS dispatches -- comments, matching,
  // transaction lock -- are not staged coding and are not flagged.
  export function updateTransactionsList(payload) {
    return { type: constants.UPDATE_TRANSACTIONS, payload, staged: true };
  }

  export function deselectTransaction(payload) {
    return { type: constants.DESELECT_TRANSACTION, payload };
  }

  export function selectTransaction(payload) {
    return { type: constants.SELECT_TRANSACTION, payload };
  }

  export function selectTransactions(payload) {
    return { type: constants.SELECT_TRANSACTIONS, payload };
  }

  export function removeMatchButton(payload) {
    return { type: constants.REMOVE_MATCH_BUTTON, payload };
  }

  export function restoreMatchButton(payload) {
    return { type: constants.RESTORE_MATCH_BUTTON, payload };
  }

  export function updateMatches(payload) {
    return { type: constants.UPDATE_MATCHES, payload };
  }

  export function updateFilterBy (filterBy) {
    return { type: constants.UPDATE_FILTERBY, payload: filterBy };
  }

  export function matchRAHTransaction(bookId, transaction) {
    const matchId = { MatchId: transaction.MatchId };

    return function(dispatch) {
      dispatch({ type: constants.IS_SAVING, payload: transaction.BankDataGUID });
      axios.post(`${constants.ROOT_URL}/RAH/${bookId}/match/${transaction.BankDataGUID}`, matchId,
      { headers: { authorizationtoken: localStorage.getItem("token") } })
        .then(() => {
              dispatch({ type: constants.IS_SAVED, payload: transaction.BankDataGUID });
              dispatch({ type: constants.UPDATE_TRANSACTIONS, payload: transaction });
      }).catch((error) => {
        dispatch({ type: constants.IS_SAVED, payload: transaction.BankDataGUID });
        if (process.env.BANKDATA_ENV === 'production') {
          dispatch(Notifications.warning({ message: 'Could not match transaction' }));
        } else {
          const errorMsg = `${constants.ROOT_URL}/RAH/${bookId}/match/${transaction.BankDataGUID} -- Response: ${(error.response) ? error.response.data.message : error.toString()}`;
          dispatch({ type: constants.SET_ERROR, payload: errorMsg });
        }
      });
    }
  }

    export function matchR1Transaction(bookId, transaction, match) {
    return function(dispatch, getState) {
      dispatch({ type: constants.IS_SAVING, payload: transaction.BankDataGUID });
      axios.post(`${constants.ROOT_URL}/R1/${bookId}/match/${transaction.BankDataGUID}`, { match },
      { headers: { authorizationtoken: localStorage.getItem("token") } })
        .then((response) => {
              dispatch({ type: constants.IS_SAVED, payload: transaction.BankDataGUID });
              const failure = matchFailure(response.data);
              if (failure) {
                // Only a refusal is known not to have been applied, so only a refusal is
                // unwound. Anything else may have left a receipt or payment behind in R1,
                // and clearing the coding and freeing the match would invite the user to
                // allocate it a second time -- the same reasoning as the .catch below.
                // Those get the message and the row is left as it is.
                if (failure.refused) unwindMatch(dispatch, getState, transaction);
                dispatch(Notifications.warning({ message: failure.message }));
                return;
              }
              dispatch({ type: constants.UPDATE_TRANSACTIONS, payload: transaction });
      }).catch((error) => {
        dispatch({ type: constants.IS_SAVED, payload: transaction.BankDataGUID });
        // Deliberately not unwound. A refusal arrives as a 200 with an error body and
        // is handled above, so this branch is left with the auth failures raised
        // before the handler runs (asyncMiddleware answers those 401) and network
        // failures -- timeout, 504, dropped connection. The second kind cannot be told
        // from the first here, and on a timeout R1 may well have created the receipt
        // or payment already, so freeing the match would invite the user to allocate
        // it a second time. The row is left as it is and the next poll settles it.
        if (process.env.BANKDATA_ENV === 'production') {
          dispatch(Notifications.warning({ message: GENERIC_MATCH_ERROR }));
        } else {
          const errorMsg = `${constants.ROOT_URL}/R1/${bookId}/match/${transaction.BankDataGUID} -- Response: ${(error.response) ? error.response.data.message : error.toString()}`;
          dispatch({ type: constants.SET_ERROR, payload: errorMsg });
        }
      });
    }
  }

  export function ruleTransaction(transaction) {
    const bookId = localStorage.getItem("bookid");
    return function(dispatch) {
      dispatch({ type: constants.IS_SAVING, payload: transaction.BankDataGUID });
      axios.post(`${constants.ROOT_URL}/RAH/${bookId}/rule/${transaction.BankDataGUID}`, {},
      { headers: { authorizationtoken: localStorage.getItem("token") } })
        .then(() => {
              dispatch({ type: constants.IS_SAVED, payload: transaction.BankDataGUID });
      }).catch((error) => {
        if (process.env.BANKDATA_ENV === 'production') {
          dispatch(Notifications.warning({ message: 'Could not accept rule' }));
        } else {
          const errorMsg = `${constants.ROOT_URL}/RAH/${bookId}/rule/${transaction.BankDataGUID} -- Response: ${(error.response) ? error.response.data.message : error.toString()}`;
          dispatch({ type: constants.SET_ERROR, payload: errorMsg });
        }
      });
    }
  }

   export function getTransactions(bookId, account, dateRange) {
      const isRah = (localStorage.getItem("authtype") === 'RAH') && !localStorage.getItem("cognitotoken");
      if (account.value) {
          return function(dispatch) {
              // Set loading status before starting fetch
              dispatch({ type: constants.IS_LOADING_TRANSACTIONS, payload: true });
  
              const headers = { 
                authorizationtoken: localStorage.getItem("token"), 
                cognitotoken: localStorage.getItem("cognitotoken"), 
                authtype: localStorage.getItem("authtype") 
              };

              fetchTransactionsWithRules(bookId, dateRange, account, headers)
              .then(async response => {
                  const transactionLength = response && response.transactions.length || 0;
                  let payload = response && response.transactions.map((item, index) => shapeData(item, transactionLength, index, account.lockoffDate)) || [];
                  const matches = response.matches || [];
                  let filename;
                  if(isRah) {
                      await axios.get(`${constants.ROOT_URL}/books/${bookId}/details`,
                      { headers })
                      .then(response => {
                          filename = response.data.LedgerData ? response.data.LedgerData.Description : '';
                          if (response.data.LedgerData && response.data.LedgerData.HostedClosingDate) {
                              const closingDate = moment(response.data.LedgerData.HostedClosingDate);
                              if (closingDate.isValid()) {
                                  payload = payload.map(txn => moment(txn.TransactionDate, 'DD MMM YY').isSameOrBefore(closingDate)
                                  ? { ...txn, locked: true, lockedDetails: 'Closed off in RAH' } : txn);
                              }
                          }
                      })
                  }
                  dispatch({ type: constants.GET_TRANSACTIONS, payload, filename, matches, timeStamp: moment().unix() });
                  dispatch({ type: constants.SELECT_TRANSACTIONS, payload: [] });
                  // Set loading status to false after successful fetch
                  dispatch({ type: constants.IS_LOADING_TRANSACTIONS, payload: false });
              })
              .catch((error) => {
                  // Set loading status to false on error
                  dispatch({ type: constants.IS_LOADING_TRANSACTIONS, payload: false });
                  if (process.env.BANKDATA_ENV === 'production') {
                      dispatch(Notifications.warning({ message: 'Could not fetch transactions' }));
                  } else {
                      const errorMsg = `GET: ${constants.ROOT_URL}/books/${bookId}/transactions/${dateRange} -- Response: ${(error.response) ? error.response.data.message : error.toString()}`;
                      dispatch({ type: constants.SET_ERROR, payload: errorMsg });
                  }
              });
          };
      } else {
          return { type: constants.GET_TRANSACTIONS, payload: [] };
      }
  }

  export function codeTransaction(codingDetails, dateRange, timeStamp, noSync, account, skipUpdate = false) {
    const isRah = (localStorage.getItem("authtype") === 'RAH') && !localStorage.getItem("cognitotoken");

    return function(dispatch) {
      !noSync && dispatch({ type: constants.IS_SAVING, payload: codingDetails.BankDataGuid });
      dispatch({ type: constants.STOP_MATCH_LOADING, payload: codingDetails.BankDataGuid });

      if (codingDetails.Coding.length === 1) {
        codingDetails.AccountCode = codingDetails.Coding[0].AccountCode;
        codingDetails.Quantity = codingDetails.Coding[0].Quantity;
        codingDetails.GstCode = codingDetails.Coding[0].GstCode;
        codingDetails.notCoded = undefined;
      }

      setTimeout(() =>
        dispatch({
          type: constants.SET_CODED,
          payload: codingDetails,
          coded: Boolean(codingDetails.Coding.length) ? 'QC' : undefined,
        })
      );

      axios
        .post(`${constants.ROOT_URL}/transactions/coded/`, codingDetails, {
          headers: {
            authorizationtoken: localStorage.getItem('token'),
            cognitotoken: localStorage.getItem('cognitotoken'),
            authtype: localStorage.getItem('authtype'),
          },
        })
        .then(() => {
          // Only now is the coding on the server, so the row can take background
          // refreshes again. Clearing it when the request went out left a window where
          // a poll GET issued before this POST could still answer with the uncoded copy
          // and land after it, putting the row back to square one (RWR-19694).
          dispatch({ type: constants.CODING_SETTLED, payload: codingDetails.BankDataGuid });
          if (!isRah) {
            dispatch({ type: constants.IS_SAVED, payload: codingDetails.BankDataGuid });
          } else {
            dispatch({ type: constants.SET_POLL_INTERVAL, payload: 1, syncing: false });
          }

          const timeLapse = moment().unix() - timeStamp;
          if (!isRah && !skipUpdate) {
            const headers = {
              authorizationtoken: localStorage.getItem('token'),
              cognitotoken: localStorage.getItem('cognitotoken'),
              authtype: localStorage.getItem('authtype'),
              lastgetelapsed: timeLapse,
            };

            fetchTransactionsWithRules(codingDetails.BookId, dateRange, account, headers)
              .then((response) => {
                const transactionLength = response.transactions.length;
                const payload = response.transactions.map((item, index) => {
                  return shapeData(item, transactionLength, index);
                });
                dispatch({ type: constants.NEW_TRANSACTIONS, payload, timeStamp: moment().unix() });
              })
              .catch((err) => {
                dispatch(Notifications.warning({ message: 'Fetch new transactions failed' }));
              });
          }
        })
        .catch(() => {
          dispatch({ type: constants.TRANSACTION_CODING_ERROR, payload: codingDetails });
          dispatch({ type: constants.IS_SAVED, payload: codingDetails.BankDataGuid });
          // The coding never reached the server, so the row has to stop being held back
          // as well. Nothing else here puts it right -- TRANSACTION_CODING_ERROR only
          // feeds the counter badge in OptionMenu -- so leaving it staged would keep the
          // SET_CODED look above on a row the server still has as uncoded, with no way
          // for a refresh to correct it before a reload. Clearing it leaves the failure
          // path as it was before RWR-19694: the next poll settles the row.
          dispatch({ type: constants.CODING_SETTLED, payload: codingDetails.BankDataGuid });
          dispatch(Notifications.warning({ message: 'Transaction allocation not saved' }));
        });
    };
  }

  export function postRule(rule, bookId) {
    return function(dispatch) {
        axios.post(`${constants.ROOT_URL}/books/${bookId}/rules/`, rule,
        { headers: { authorizationtoken: localStorage.getItem("token"), cognitotoken: localStorage.getItem("cognitotoken"), authtype: localStorage.getItem("authtype") } })
          .then(response => {
              dispatch({ type: constants.NO_ACTION });
      })
      .catch((error) => {
        if (process.env.BANKDATA_ENV === 'production') {
          dispatch(Notifications.warning({ message: 'Unable to save rule' }));
        } else {
          const errorMsg = `POST: ${constants.ROOT_URL}/books/${bookId}/rules/ -- Response: ${(error.response) ? error.response.data.message : error.toString()}`;
          dispatch({ type: constants.SET_ERROR, payload: errorMsg });
        }
      });
    };
  }

  export function clearCanceledSplit(transaction) {
    return { type: constants.UPDATE_TRANSACTIONS, payload: [transaction] }
  };

  export function removeSelectedTransactions(removeSelected, closeRemoveModal, dateRange, account) {
    return function(dispatch) {
        axios.post(`${constants.ROOT_URL}/books/upload/delete/`, removeSelected,
        { headers: { authorizationtoken: localStorage.getItem("token"), cognitotoken: localStorage.getItem("cognitotoken"), authtype: localStorage.getItem("authtype") } })
          .then(response => {
              const noTransactions = removeSelected.length;
              dispatch(Notifications.success({ message: `${noTransactions} transaction${noTransactions > 1 ? 's' : ''} removed` }));

              const headers = { 
                authorizationtoken: localStorage.getItem("token"), 
                cognitotoken: localStorage.getItem("cognitotoken"), 
                authtype: localStorage.getItem("authtype") 
              };

              fetchTransactionsWithRules(localStorage.getItem("bookid"), dateRange, account, headers)
                .then(response => {
                  const transactionLength = response.transactions.length;
                  let payload = response.transactions.map((item, index) => shapeData(item, transactionLength, index, account.lockoffDate));
                  dispatch({ type: constants.GET_TRANSACTIONS, payload, timeStamp: moment().unix() });
                  closeRemoveModal();
                }).catch((err) => console.log(err));
      })
      .catch((error) => {
        if (process.env.BANKDATA_ENV === 'production') {
          dispatch(Notifications.warning({ message: 'Unable to remove transactions' }));
        } else {
          const errorMsg = `POST: ${constants.ROOT_URL}/books/upload/delete/ -- Response: ${(error.response) ? error.response.data.message : error.toString()}`;
          dispatch({ type: constants.SET_ERROR, payload: errorMsg });
        }
      });
    };
  }

  export function toggleTransactionLock(transaction, bookId) {
    return function(dispatch) {
      axios
        .get(
          `${constants.ROOT_URL}/books/${bookId}/transaction/${
            transaction.BankDataGUID
          }/transactionlock`,
          {
            headers: {
              authorizationtoken: localStorage.getItem('token'),
              cognitotoken: localStorage.getItem('cognitotoken'),
              authtype: localStorage.getItem('authtype'),
            },
          }
        )
        .then((response) => {
          dispatch({
            type: constants.UPDATE_TRANSACTIONS,
            payload: transaction,
          });
        })
        .catch((error) => {
          dispatch(Notifications.warning({ message: 'Unable to update the transaction' }));
        });
    };
  }

  export function startMatchLoading(payload) {
    return { type: constants.START_MATCH_LOADING, payload };
  }

  export function stopMatchLoading(payload) {
    return { type: constants.STOP_MATCH_LOADING, payload };
  }

  export function sortRows(row) {
    return { type: constants.SORT_ROWS, payload: row }
  }

  export function codedByR1Rule(row) {
    return function(dispatch) {
      dispatch({
        type: constants.CODED_BY_R1_RULE,
        payload: { ...row,
          BankDataGUID: row.BankDataGUID,
          AccountCode: "-2",
          lockedDetails: 'Coded by R1 rule',
          locked: true },
      });
    };
  }



// WEBPACK FOOTER //
// ./src/Actions/transactions.js